import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateServiceSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { SERVICES } from '@/lib/services';

const process = [
  { step: "01", title: "Discovery", desc: "We dig into your business, audience and unit economics. No assumptions." },
  { step: "02", title: "Strategy", desc: "Positioning, channels, creative angles, KPIs. A growth architecture, not a deck." },
  { step: "03", title: "Build & Ship", desc: "Code, design, copy, campaigns — produced in one studio, on one timeline." },
  { step: "04", title: "Scale", desc: "We instrument, test and compound. The system gets smarter every week." },
];

export default function Services() {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      generateBreadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" }
      ]),
      ...SERVICES.map(s => generateServiceSchema({ name: s.title, description: s.short })),
    ]
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="services-page">
      <SEO
        title="Services — Performance Marketing, E-commerce, Web, CRM, Packaging & Branding"
        description="Wizbang offers performance marketing, e-commerce development, web development, custom CRM development, package designing and branding services for ambitious brands."
        keywords="performance marketing agency, ecommerce development India, web development services, custom CRM development, packaging design agency, branding agency, creative media technology agency"
        url="/services"
        structuredData={structuredData}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow mb-6">Our services</div>
          <h1 className="font-syne text-5xl sm:text-6xl lg:text-8xl font-bold text-[#0A0A0A] mb-8 max-w-5xl tracking-tight leading-[0.95]">
            Build. <span className="font-editorial text-[#ff5a1f]">Market.</span> Scale.
          </h1>
          <p className="text-[#5B5B5B] text-lg md:text-xl max-w-2xl leading-relaxed">
            Six disciplines under one roof. From the first wireframe to the last conversion — we own every link in your growth chain.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#FAFAF7]" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, idx) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-tile-${s.slug}`}
                className="group bg-white border rounded-2xl p-8 hover:border-[#ff5a1f] hover:shadow-xl transition-all duration-500 flex flex-col card-tilt"
                style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#ff5a1f]/10 flex items-center justify-center group-hover:bg-[#ff5a1f] transition-colors">
                    <s.icon className="w-7 h-7 text-[#ff5a1f] group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-mono text-xs text-[#8A8A8A]">0{idx + 1}</span>
                </div>
                <h2 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight group-hover:text-[#ff5a1f] transition-colors">
                  {s.title}
                </h2>
                <p className="font-editorial text-[#ff5a1f] mb-4">{s.tagline}</p>
                <p className="text-[#5B5B5B] text-sm leading-relaxed mb-8 flex-1">{s.short}</p>
                <div className="inline-flex items-center gap-2 text-[#0A0A0A] font-semibold text-sm">
                  Learn more <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 max-w-2xl">
            <div className="eyebrow mb-5">How we work</div>
            <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] tracking-tight">
              No <span className="font-editorial text-[#ff5a1f]">surprises.</span> No vanity decks.
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {process.map((step, idx) => (
              <div key={idx} className="relative">
                {idx < process.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-full w-full h-[2px] bg-[#E8E5DD]" />
                )}
                <div className="font-mono text-3xl font-bold text-[#ff5a1f] mb-4">{step.step}</div>
                <h3 className="font-syne text-xl font-bold text-[#0A0A0A] mb-3 tracking-tight">{step.title}</h3>
                <p className="text-[#5B5B5B] text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#FAFAF7] relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-syne text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-8 tracking-tight">
            Pick a service. <br />
            <span className="font-editorial text-[#ff5a1f]">Or bring us your whole problem.</span>
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-12">
            Free 40-minute strategy call. We'll come prepared.
          </p>
          <Link to="/book-appointment"
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
            Book your call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
